export interface ReportFinding {
  finding_id?: string
  domain?: string
  severity?: number | string
  condition_level?: string | boolean | null
  summary?: string | null
  observation?: string
  area?: string
  standard_id?: string | null
  standard_ref?: string
  image?: string
  image_url?: string
  evidence_url?: string
}

export interface ReportAction {
  action_id?: string
  finding_id?: string
  title?: string | null
  description?: string | null
  status?: string | null
  due_date?: string | null
  completed_date?: string | null
  owner_role?: string | null
  assigned_to_user_id?: string | null
}

export interface ScoreNode {
  overall_score?: number
  grade?: string | null
  cms_outcome?: string
  cms_outcome_label?: string
}

export interface ReportPayload {
  governance_context?: {
    accreditation_framework?: string | null
    accreditation_framework_label?: string | null
    framework_source?: string | null
  }
  findings?: ReportFinding[]
  corrective_actions?: ReportAction[]
  score?: number | ScoreNode
  cms_outcome?: string
  cms_outcome_label?: string
  executive_summary?: string
  facility_branding?: {
    facility_name?: string | null
    org_display_name?: string | null
    address?: string | null
    city?: string | null
    state?: string | null
    zip_code?: string | null
    license_number?: string | null
    accreditation_framework?: string | null
    accreditation_framework_label?: string | null
  }
  report_meta?: {
    generated_at?: string
    report_version?: number
    schema_version?: string
    visit_id?: string
    accreditation_framework?: string
    accreditation_framework_label?: string
  }
  visit?: {
    survey_date?: string | null
    visit_type?: string | null
    accreditation_framework_snapshot?: string | null
    accreditation_framework_label?: string | null
  }
  [key: string]: unknown
}

export interface DomainReportSection {
  code: string
  label: string
  findings: ReportFinding[]
  highCount: number
  moderateCount: number
  lowCount: number
}

export interface ReferenceReportViewModel {
  cover: {
    title: string
    facilityName: string
    organizationName: string | null
    address: string | null
    surveyDate: string | null
    visitType: string | null
    reportVersion: number | null
    generatedAt: string | null
    schemaVersion: string | null
    accreditationFramework: string
    accreditationFrameworkLabel: string
  }
  score: ScoreNode
  outcomeLabel: string | null
  outcomeCode: string | null
  executiveSummary: string
  immediateAttention: ReportFinding[]
  domains: DomainReportSection[]
  correctiveActions: ReportAction[]
  appendixItems: ReportFinding[]
  totalFindings: number
}

export const DOMAIN_LABELS: Record<string, string> = {
  IC: 'Infection Control',
  SPD: 'Sterile Processing',
  ENV: 'Environment of Care',
  LSC: 'Life Safety Code',
  MED: 'Medication Management',
  COMP: 'Compliance & Rights',
  WT: 'Waived Testing',
  CR: 'Clinical Records',
  HR: 'Human Resources',
  EM: 'Emergency Management',
  ADA: 'Accessibility',
  WASTE: 'Medical Waste',
  OTHER: 'Other',
}

const ACCREDITATION_FRAMEWORK_LABELS: Record<string, string> = {
  cms: 'CMS',
  aaahc: 'AAAHC',
  tjc: 'TJC',
  quad_a: 'QUAD A',
  achc: 'ACHC',
  state: 'State',
  other: 'Other',
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function normalizeAccreditationFramework(value: unknown): string {
  if (typeof value !== 'string') return 'cms'
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (normalized === 'cms' || normalized === 'aaahc' || normalized === 'tjc' || normalized === 'quad_a' || normalized === 'achc' || normalized === 'state' || normalized === 'other') {
    return normalized
  }
  if (normalized === 'quad' || normalized === 'quada') return 'quad_a'
  return 'cms'
}

function accreditationFrameworkLabel(value: string): string {
  return ACCREDITATION_FRAMEWORK_LABELS[normalizeAccreditationFramework(value)] ?? 'CMS'
}

export function normalizeSeverity(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const lower = value.toLowerCase()
    if (lower.includes('high') || lower === '3') return 3
    if (lower.includes('moderate') || lower === '2') return 2
    if (lower.includes('low') || lower === '1') return 1
  }
  return 1
}

export function severityLabel(value: unknown): string {
  const severity = normalizeSeverity(value)
  if (severity >= 3) return 'High'
  if (severity === 2) return 'Moderate'
  return 'Low'
}

function scoreNode(payload: ReportPayload): ScoreNode {
  if (typeof payload.score === 'number') {
    return {
      overall_score: payload.score,
      cms_outcome: payload.cms_outcome,
      cms_outcome_label: payload.cms_outcome_label,
    }
  }
  if (asRecord(payload.score)) return payload.score as ScoreNode
  return {
    cms_outcome: payload.cms_outcome,
    cms_outcome_label: payload.cms_outcome_label,
  }
}

function normalizeFinding(value: unknown): ReportFinding | null {
  if (!asRecord(value)) return null
  return {
    finding_id: asString(value.finding_id) ?? undefined,
    domain: asString(value.domain) ?? undefined,
    severity: typeof value.severity === 'string' || typeof value.severity === 'number' ? value.severity : undefined,
    condition_level: typeof value.condition_level === 'string' || typeof value.condition_level === 'boolean' ? value.condition_level : null,
    summary: asString(value.summary),
    observation: asString(value.observation) ?? undefined,
    area: asString(value.area) ?? undefined,
    standard_id: asString(value.standard_id),
    standard_ref: asString(value.standard_ref) ?? undefined,
    image: asString(value.image) ?? undefined,
    image_url: asString(value.image_url) ?? undefined,
    evidence_url: asString(value.evidence_url) ?? undefined,
  }
}

function normalizeAction(value: unknown): ReportAction | null {
  if (!asRecord(value)) return null
  return {
    action_id: asString(value.action_id) ?? undefined,
    finding_id: asString(value.finding_id) ?? undefined,
    title: asString(value.title),
    description: asString(value.description),
    status: asString(value.status),
    due_date: asString(value.due_date),
    completed_date: asString(value.completed_date),
    owner_role: asString(value.owner_role),
    assigned_to_user_id: asString(value.assigned_to_user_id),
  }
}

function findingDomain(finding: ReportFinding): string {
  const raw = finding.domain?.trim()
  if (!raw) return 'OTHER'
  return raw.split(',')[0]?.trim() || 'OTHER'
}

function conditionRequiresImmediateAttention(conditionLevel: ReportFinding['condition_level']): boolean {
  if (conditionLevel === true) return true
  if (typeof conditionLevel !== 'string') return false
  const lower = conditionLevel.toLowerCase()
  return lower.includes('condition') || lower.includes('immediate')
}

function hasAppendixEvidence(finding: ReportFinding): boolean {
  return Boolean(finding.image || finding.image_url || finding.evidence_url)
}

function buildAddress(payload: ReportPayload): string | null {
  const branding = payload.facility_branding
  if (!branding) return null
  const cityStateZip = [branding.city, branding.state, branding.zip_code].filter(Boolean).join(', ')
  return [branding.address, cityStateZip].filter(Boolean).join(' | ') || null
}

function groupDomains(findings: ReportFinding[]): DomainReportSection[] {
  const groups = new Map<string, ReportFinding[]>()
  for (const finding of findings) {
    const code = findingDomain(finding)
    groups.set(code, [...(groups.get(code) ?? []), finding])
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, domainFindings]) => ({
      code,
      label: DOMAIN_LABELS[code] ?? code,
      findings: domainFindings,
      highCount: domainFindings.filter((finding) => normalizeSeverity(finding.severity) >= 3).length,
      moderateCount: domainFindings.filter((finding) => normalizeSeverity(finding.severity) === 2).length,
      lowCount: domainFindings.filter((finding) => normalizeSeverity(finding.severity) <= 1).length,
    }))
}

export function buildReferenceReportViewModel(payload: Record<string, unknown> | null): ReferenceReportViewModel | null {
  if (!payload) return null
  const reportPayload = payload as ReportPayload
  const findings = Array.isArray(reportPayload.findings)
    ? reportPayload.findings.map(normalizeFinding).filter((finding): finding is ReportFinding => Boolean(finding))
    : []
  const correctiveActions = Array.isArray(reportPayload.corrective_actions)
    ? reportPayload.corrective_actions.map(normalizeAction).filter((action): action is ReportAction => Boolean(action))
    : []
  const score = scoreNode(reportPayload)
  const facilityName =
    asString(reportPayload.facility_branding?.facility_name) ??
    asString(reportPayload.facility_branding?.org_display_name) ??
    'ASC Facility'
  const accreditationFramework = normalizeAccreditationFramework(
    reportPayload.visit?.accreditation_framework_snapshot ??
    reportPayload.governance_context?.accreditation_framework ??
    reportPayload.report_meta?.accreditation_framework ??
    reportPayload.facility_branding?.accreditation_framework,
  )
  const frameworkLabel =
    asString(reportPayload.visit?.accreditation_framework_label) ??
    asString(reportPayload.governance_context?.accreditation_framework_label) ??
    asString(reportPayload.report_meta?.accreditation_framework_label) ??
    asString(reportPayload.facility_branding?.accreditation_framework_label) ??
    accreditationFrameworkLabel(accreditationFramework)

  return {
    cover: {
      title: 'ASC Mock Survey Consolidated Report',
      facilityName,
      organizationName: asString(reportPayload.facility_branding?.org_display_name),
      address: buildAddress(reportPayload),
      surveyDate: asString(reportPayload.visit?.survey_date),
      visitType: asString(reportPayload.visit?.visit_type),
      reportVersion: asNumber(reportPayload.report_meta?.report_version),
      generatedAt: asString(reportPayload.report_meta?.generated_at),
      schemaVersion: asString(reportPayload.report_meta?.schema_version),
      accreditationFramework,
      accreditationFrameworkLabel: frameworkLabel,
    },
    score,
    outcomeLabel: score.cms_outcome_label ?? reportPayload.cms_outcome_label ?? null,
    outcomeCode: score.cms_outcome ?? reportPayload.cms_outcome ?? null,
    executiveSummary: asString(reportPayload.executive_summary) ?? 'Executive summary has not been drafted yet.',
    immediateAttention: findings.filter(
      (finding) => normalizeSeverity(finding.severity) >= 3 || conditionRequiresImmediateAttention(finding.condition_level),
    ),
    domains: groupDomains(findings),
    correctiveActions,
    appendixItems: findings.filter(hasAppendixEvidence),
    totalFindings: findings.length,
  }
}
